import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

/**
 * Component size flavors:
 * - 'standard': full desktop layout with all information
 * - 'short': mobile layout with only essential information, less important details shrunk/hidden
 */
export type ComponentSize = 'standard' | 'short'

const MOBILE_BREAKPOINT = '(max-width: 768px)'

const ComponentSizeContext = createContext<ComponentSize>('standard')

/**
 * Hook to detect whether a CSS media query matches.
 * Extracted from Layout.tsx for reuse.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia(query).matches
    }
    return false
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mediaQuery = window.matchMedia(query)
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches)
    mediaQuery.addEventListener('change', handler)
    setMatches(mediaQuery.matches)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [query])

  return matches
}

/**
 * Provider that determines the component size based on viewport width.
 * Wrap your app (or a subtree) with this to enable useComponentSize() below.
 */
export function ComponentSizeProvider({ children, breakpoint }: { children: ReactNode, breakpoint?: string }) {
  const isMobile = useMediaQuery(breakpoint ?? MOBILE_BREAKPOINT)
  const size: ComponentSize = isMobile ? 'short' : 'standard'

  return (
    <ComponentSizeContext.Provider value={size}>
      {children}
    </ComponentSizeContext.Provider>
  )
}

/**
 * Hook to get the current component size ('standard' or 'short').
 * Must be used inside a <ComponentSizeProvider>.
 */
export function useComponentSize(): ComponentSize {
  return useContext(ComponentSizeContext)
}

/**
 * Renders different content based on the current component size.
 *
 * Usage:
 *   <Responsive
 *     standard={<FullDetailedView />}
 *     short={<CompactMobileView />}
 *   />
 */
export function Responsive({ standard, short }: { standard: ReactNode, short: ReactNode }) {
  const size = useComponentSize()
  return <>{size === 'standard' ? standard : short}</>
}

/**
 * Only renders children in 'standard' (desktop) mode.
 */
export function StandardOnly({ children }: { children: ReactNode }) {
  const size = useComponentSize()
  if (size !== 'standard') return null
  return <>{children}</>
}

/**
 * Only renders children in 'short' (mobile) mode.
 */
export function ShortOnly({ children }: { children: ReactNode }) {
  const size = useComponentSize()
  if (size !== 'short') return null
  return <>{children}</>
}
